import { inject, Injectable, Injector } from '@angular/core';
import { Action, State, type NgxsOnInit, type StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  catchError,
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  tap,
} from 'rxjs';

import { syncLoadProgress } from '@cosmos/state';
import { isEqual } from '@cosmos/util-common/is-equal';
import { AuthFacade } from '@esp/auth/data-access-auth';
import { LookupsActions } from '@esp/lookup/data-access-lookup/actions';
import {
  LOOKUP_TYPES_STATE_TOKEN,
  type LookupTypesStateModel,
} from '@esp/lookup/data-access-lookup/state-token';

import { LookupsApiService } from '../services/lookups-api.service';

type LocalStateContext = StateContext<LookupTypesStateModel>;

@State({
  name: LOOKUP_TYPES_STATE_TOKEN,
  defaults: {
    lookups: {},
  },
})
@Injectable()
export class LookupTypesState implements NgxsOnInit {
  private readonly _injector = inject(Injector);
  private _lookupsApi = inject(LookupsApiService);
  private _auth = inject(AuthFacade);

  ngxsOnInit(ctx: LocalStateContext) {
    this._auth.profile$
      .pipe(
        map((user) => user?.Id),
        distinctUntilChanged()
      )
      .subscribe({
        next: (userId) => {
          if (userId) {
            ctx.dispatch(new LookupsActions.LoadAll());
          } else {
            ctx.dispatch(new LookupsActions.ClearAll());
          }
        },
      });
  }

  /**
   * Commands
   */

  @Action(LookupsActions.LoadAll)
  loadAllLookups(ctx: LocalStateContext) {
    return this._lookupsApi.getLookups().pipe(
      tap((result) => {
        const lookups = ctx.getState().lookups;
        ctx.patchState({ lookups: { ...lookups, ...result } });
      }),
      syncLoadProgress(ctx)
    );
  }

  @Action(LookupsActions.ClearAll)
  clearAllLookups(ctx: LocalStateContext) {
    // Skip the reset when lookups are already empty, to avoid an unnecessary emission.
    const newLookups = {};
    if (isEqual(ctx.getState().lookups, newLookups)) return;
    ctx.setState({ lookups: {} });
  }

  @Action(LookupsActions.Load)
  loadType(ctx: LocalStateContext, { type }: LookupsActions.Load) {
    return this._lookupsApi.getType(type).pipe(
      catchError(() => of([])),
      tap((result) => {
        ctx.setState(
          patch({
            lookups: patch<LookupTypesStateModel['lookups']>({
              [type]: result,
            }),
          })
        );
      })
    );
  }

  @Action([LookupsActions.LoadAll, LookupsActions.ReloadTagTypes])
  async loadTagTypes(ctx: LocalStateContext) {
    const lookupTypesService = await this._injectLookupTypesService();
    const result = await firstValueFrom(lookupTypesService.getTagTypes(), {
      defaultValue: undefined,
    });
    if (result === undefined) return;
    ctx.setState(
      patch({
        lookups: patch({ TagTypes: result }),
      })
    );
  }

  @Action([LookupsActions.LoadAll, LookupsActions.ReloadRelationships])
  async loadLinkRelationshipTypes(ctx: LocalStateContext) {
    const lookupTypesService = await this._injectLookupTypesService();
    const result = await firstValueFrom(
      lookupTypesService.getLinkRelationships(),
      { defaultValue: undefined }
    );
    if (result === undefined) return;
    ctx.setState(
      patch({
        lookups: patch({ LinkRelationshipTypes: result }),
      })
    );
  }

  private async _injectLookupTypesService() {
    const { LookupTypesApiService } = await import(
      '../services/lookup-types-api.service'
    );
    return this._injector.get(LookupTypesApiService);
  }
}
